import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { BoundedSearch, CheckBold, CheckGroup, LeftButton, RightButton, HR, ShadowBox, ShiftShadowBox, GroupsContainer } from '../styled';
import { filter } from '../util';
// Just enough width to prevent borders from the underlying Dropdown from showing
const OverlayBox = styled(ShiftShadowBox) `
  width: 305px;
`;
const allChecked = (options, selected) => !!selected.length &&
    options.filter(o => !o.disabled).map(o => o.value).length === selected.length;
const groupFullySelected = (group, options, selected) => options
    .filter(o => !o.disabled)
    .map(o => o.value)
    .filter(group.onSelectFilter)
    .every(e => selected.includes(e));
export const MultiSelectOverlay = ({ displayName, onConfirm, onSelect, onReset, options, selected = [], //Can be a string, or a groups tag keyword
showAll, showSearch, shifted, style, visible, groups, showFooter = true }) => {
    const [all, setAll] = useState(allChecked(options, selected));
    const [search, setSearch] = useState('');
    const [groupOptionsDisabled, setGroupOptionsDisabled] = useState(false);
    const filteredOptions = filter(search, options);
    let input;
    if (showSearch) {
        const searchInput = useRef(null);
        useEffect(() => {
            var _a;
            if (visible) {
                (_a = searchInput.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }, [visible, searchInput.current]);
        const onSearch = (e) => setSearch(e.target.value || '');
        input = (React.createElement(BoundedSearch, { ref: searchInput, onChange: onSearch, placeholder: `Search ${displayName}`, value: search }));
    }
    const groupDescriptors = new Map([]);
    groups === null || groups === void 0 ? void 0 : groups.forEach(group => {
        groupDescriptors.set(group.id, {
            state: useState(false),
            group: group
        });
    });
    // Expand each selection that represents a group into a list of ID's
    const expandedSelections = selected
        .reduce((prev, currValue) => {
        const descriptor = groupDescriptors.get(currValue);
        if (descriptor) {
            const group = descriptor.group;
            const groupItems = options
                .filter(o => !o.disabled)
                .map(o => o.value)
                .filter(group.onSelectFilter);
            return [...prev, ...groupItems];
        }
        return [...prev, currValue];
    }, [])
        .filter(e => !groupDescriptors.has(e));
    // Keep the group states in sync with the expanded selections
    for (const [key, descriptor] of groupDescriptors.entries()) {
        const [_, setGroupSelected] = descriptor.state;
        useEffect(() => {
            const fullySelected = groupFullySelected(descriptor.group, options, expandedSelections);
            const groupInSelected = selected.find(e => e == key) != undefined;
            setGroupSelected(fullySelected && groupInSelected);
        }, [expandedSelections, selected]);
    }
    const groupSelected = (group) => {
        const descriptor = groupDescriptors.get(group.id);
        if (descriptor) {
            const [groupSelected] = descriptor.state;
            return groupSelected;
        }
        return false;
    };
    const findGroupName = (value) => {
        for (const [key, descriptor] of groupDescriptors.entries()) {
            const [groupSelected] = descriptor.state;
            if (groupSelected && descriptor.group.onSelectFilter(value, 0, [])) {
                return key;
            }
        }
        return '';
    };
    const onGroupSelected = (group) => {
        return (e) => {
            const descriptor = groupDescriptors.get(group.id);
            // First set the state variable to match the checkbox state
            if (descriptor) {
                const [_, setGroupSelected] = descriptor.state;
                setGroupSelected(e.target.checked);
            }
            // If this group represents the "All" group, and we've just checked it, unselect and disable the other groups (that also aren't select all)
            if (e.target.checked && group.isSelectAll) {
                setGroupOptionsDisabled(true);
                for (const [_, descriptor] of groupDescriptors.entries()) {
                    if (!descriptor.group.isSelectAll) {
                        const [_, setGroupSelected] = descriptor.state;
                        setGroupSelected(false);
                    }
                }
            }
            else if (groupOptionsDisabled) {
                setGroupOptionsDisabled(false);
            }
            // Find all the options that match the current group
            const groupItems = options
                .filter(o => !o.disabled)
                .map(o => o.value)
                .filter(group.onSelectFilter);
            // If the checkbox is selected, return this as an array of Selected, otherwise an empty array (because they are unselected)
            const values = e.target.checked
                ? groupItems.map(e => ({ value: e, group: group.id }))
                : [];
            // Using the state of the group selections (for all groups, create a list of groups that _should_ be selected) (taking into account if the current group is a selectAll or not)
            const groupsSelected = [];
            for (const [key, descriptor] of groupDescriptors.entries()) {
                const [groupSelected] = descriptor.state;
                if (groupSelected && !group.isSelectAll) {
                    groupsSelected.push(key);
                }
            }
            // Push (or splice out) the current group too based on if it's selected
            if (e.target.checked) {
                groupsSelected.push(group.id);
            }
            else {
                const index = groupsSelected.indexOf(group.id);
                if (index > -1) {
                    groupsSelected.splice(index, 1);
                }
            }
            // Create a list of all visible items (not groups) that need to be selected
            const allValues = [
                ...expandedSelections
                    .filter(item => !groupItems.includes(item))
                    .map(e => {
                    return { value: e, group: findGroupName(e) };
                }),
                ...values
            ];
            // In the list of all the items that are to be checked, reduce it down to the list of groups that it represents
            const groupsWithItems = [...new Set(allValues.map(e => e.group))];
            // Now find the difference between the list of groups that are selected, vs the list of groups that are represented by the items (but exclude the current group).
            const groupsNotRepresented = groupsSelected.filter(x => !groupsWithItems.includes(x));
            // Create a list now of all the values, and the empty groups
            const valuesAndGroups = [
                ...allValues,
                ...groupsNotRepresented.map(e => {
                    var _a, _b;
                    return ({
                        value: ((_a = groupDescriptors.get(e)) === null || _a === void 0 ? void 0 : _a.group.label) || '',
                        group: ((_b = groupDescriptors.get(e)) === null || _b === void 0 ? void 0 : _b.group.id) || ''
                    });
                })
            ];
            onSelect(valuesAndGroups);
            setSearch('');
        };
    };
    useEffect(() => {
        setAll(allChecked(options, selected));
    }, [selected]);
    const onCheckAll = (e) => {
        setAll(e.target.checked);
        const values = e.target.checked
            ? options
                .filter(o => !o.disabled)
                .map(o => ({ value: o.value, group: '' }))
            : [];
        onSelect(values);
        setSearch('');
    };
    const reset = e => {
        setSearch('');
        setGroupOptionsDisabled(false);
        onReset(e);
    };
    const hasDisabledOptions = options.some(o => o.disabled);
    const indeterminate = !!selected.length && !allChecked(options, selected);
    const Container = shifted ? OverlayBox : ShadowBox;
    const onUserSelect = (userSelected) => {
        const selection = [];
        for (const [key, descriptor] of groupDescriptors.entries()) {
            const [groupSelected, setGroupSelected] = descriptor.state;
            if (groupSelected) {
                // The group is selected
                const fullySelected = groupFullySelected(descriptor.group, options, userSelected);
                setGroupSelected(fullySelected);
                if (fullySelected) {
                    userSelected
                        .filter(e => descriptor.group.onSelectFilter(e, 0, []))
                        .forEach(e => {
                        selection.push({ value: e, group: key });
                    });
                    userSelected = userSelected.filter(e => !descriptor.group.onSelectFilter(e, 0, []));
                }
            }
        }
        onSelect([
            ...selection,
            ...userSelected.map(e => ({ value: e, group: '' }))
        ]);
        setSearch('');
    };
    return (React.createElement(Container, { style: style !== null && style !== void 0 ? style : {} },
        showSearch && input,
        showAll && !search.length && (React.createElement("div", null,
            React.createElement(CheckBold, { checked: all, indeterminate: indeterminate, onChange: onCheckAll }, `All ${hasDisabledOptions ? 'remaining ' : ''}${displayName}`))),
        React.createElement(GroupsContainer, null, groups === null || groups === void 0 ? void 0 : groups.map((group, i) => {
            return (React.createElement("div", { key: i },
                React.createElement(CheckBold, { checked: groupSelected(group), onChange: onGroupSelected(group), disabled: groupOptionsDisabled && !group.isSelectAll }, `${group.label}`)));
        })),
        React.createElement(CheckGroup, { onChange: onUserSelect, options: filteredOptions, value: expandedSelections }),
        showFooter && (React.createElement(React.Fragment, null,
            React.createElement(HR, null),
            React.createElement(React.Fragment, null,
                React.createElement(LeftButton, { onClick: reset, size: "small", type: "link" }, "Reset"),
                React.createElement(RightButton, { onClick: onConfirm, size: "small", type: "link" }, "OK"))))));
};
export default MultiSelectOverlay;
